/**
 * Loads the persisted `transcript_timings` rows for a single response so the
 * admin transcript viewer can render word-accurate, click-to-seek playback.
 *
 * Rows are written server-side by the Stage 2 forced-alignment pass; RLS grants
 * SELECT to authenticated members through the interview_data chain. The read
 * mirrors the admin frontend's direct-supabase pattern (see
 * {@link components/hooks/useCaseStudies} and
 * {@link components/hooks/useSegmentPlayback}).
 *
 * Absence is the normal Stage 1 state: alignment may not have run, or the table
 * may be empty. The hook always resolves to an ARRAY (empty when there is
 * nothing to show) so the viewer degrades to utterance-level fallback mapping
 * with zero user-facing error surface. An empty result is silent; only a
 * genuine query failure (a supabase error field or a thrown fetch) is reported
 * once via captureException.
 *
 * @module components/hooks/useTranscriptTimings
 */

import { useEffect, useRef, useState } from 'react';
import { useSupabase } from '../../lib/SupabaseProvider';
import { captureException } from '../../lib/telemetry/posthog/frontend';
import type { TranscriptTiming } from '../../types';

/** Return contract of {@link useTranscriptTimings}. */
export interface UseTranscriptTimingsResult {
  /** Timing rows for the response, or an empty array (fallback / loading). */
  timingRows: TranscriptTiming[];
}

/**
 * Fetch the `transcript_timings` rows for one response.
 *
 * @param responseId The `interview_data.id` being viewed, or undefined.
 * @returns `{ timingRows }`: the rows, or an empty array while loading, when
 *   none exist, or on error (the viewer treats an empty array as fallback).
 */
export function useTranscriptTimings(responseId?: string): UseTranscriptTimingsResult {
  const { supabase } = useSupabase();
  const [timingRows, setTimingRows] = useState<TranscriptTiming[]>([]);
  // Guard against a stale response overwriting a newer scope's rows.
  const fetchSeq = useRef(0);

  useEffect(() => {
    if (!supabase || !responseId) {
      setTimingRows([]);
      return undefined;
    }

    let cancelled = false;
    const fetchTimings = async () => {
      const seq = ++fetchSeq.current;
      try {
        const { data, error } = await supabase
          .from('transcript_timings')
          .select('*')
          .eq('interview_data_id', responseId);

        if (cancelled || seq !== fetchSeq.current) return;

        if (error) {
          // Query failure: silent fallback for the user, reported once. The
          // supabase error is widened to unknown so the message extraction is
          // type-safe regardless of how the client types the error field.
          const queryError: unknown = error;
          captureException(
            queryError instanceof Error
              ? queryError
              : new Error(typeof queryError === 'string' ? queryError : JSON.stringify(queryError)),
            'useTranscriptTimings.fetch',
            { responseId },
          );
          setTimingRows([]);
          return;
        }

        // Empty is normal (alignment not run yet): no telemetry, just fallback.
        setTimingRows((data as TranscriptTiming[] | null) ?? []);
      } catch (err) {
        if (cancelled || seq !== fetchSeq.current) return;
        captureException(
          err instanceof Error ? err : new Error(String(err)),
          'useTranscriptTimings.fetch',
          { responseId },
        );
        setTimingRows([]);
      }
    };

    void fetchTimings();
    const channel = supabase
      .channel(`transcript_timings:${responseId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'transcript_timings',
          filter: `interview_data_id=eq.${responseId}`,
        },
        () => {
          void fetchTimings();
        },
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') void fetchTimings();
      });

    return () => {
      cancelled = true;
      void supabase.removeChannel(channel);
    };
  }, [supabase, responseId]);

  return { timingRows };
}
